import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { BlogPost } from "./useBlogPosts";

export const useBookmarkArticle = () => {
  const [isLoading, setIsLoading] = useState(false);
  const { toast } = useToast();

  const checkIfBookmarked = async (postId: string): Promise<boolean> => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return false;

      const { data, error } = await supabase
        .from('bookmarks')
        .select('id')
        .eq('user_id', user.id)
        .eq('content_type', 'blog_post')
        .eq('content_id', postId)
        .single();

      return !!data;
    } catch (error) {
      return false;
    }
  };

  const toggleBookmark = async (post: BlogPost) => {
    setIsLoading(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please log in to bookmark articles.",
          variant: "destructive",
        });
        return false;
      }

      // Check if already bookmarked
      const isBookmarked = await checkIfBookmarked(post.id);

      if (isBookmarked) {
        // Remove bookmark
        const { error } = await supabase
          .from('bookmarks')
          .delete()
          .eq('user_id', user.id)
          .eq('content_type', 'blog_post')
          .eq('content_id', post.id);

        if (error) throw error;

        toast({
          title: "Bookmark Removed",
          description: "Article removed from your saved list.",
        });
        return false;
      } else {
        // Add bookmark
        const { error } = await supabase
          .from('bookmarks')
          .insert({
            user_id: user.id,
            content_type: 'blog_post',
            content_id: post.id,
            title: post.title,
            description: post.excerpt,
            metadata: {
              category: post.category,
              image: post.image,
              readTime: post.readTime,
              author: post.author,
              date: post.date,
              tags: post.tags
            }
          });

        if (error) throw error;

        toast({
          title: "Article Saved",
          description: "Article added to your reading list.",
        });
        return true;
      }
    } catch (error: any) {
      console.error('Bookmark error:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to bookmark article.",
        variant: "destructive",
      });
      return null;
    } finally {
      setIsLoading(false);
    }
  };

  return { toggleBookmark, checkIfBookmarked, isLoading };
};
